import {
  atBqnTarget,
  outputsOfBatch,
  parseClusterResponse,
  parseSystemDate,
} from "common/api";
import { parseDate } from "../utils";

export function parseMemSystemTotal(response) {
  const [header, ...rows] = response.trim().split("\n");
  const attrs = rows[0].trim().split(/\s+/);
  return Number(attrs[1]) / 1024;
}

export function parseMemUsagePoolTotal(response) {
  const [header, ...rows] = response.trim(/\s/).split("\n");
  const total = {};
  for (const row of rows) {
    const attrs = row.trim().split(/\s+/);
    if (!isNaN(attrs[0])) {
      const bqnId = attrs[0];
      if (total[bqnId]) {
        total[bqnId] += Number(attrs[2]) / 1024;
      } else {
        total[bqnId] = Number(attrs[2]) / 1024;
      }
    }
  }
  return total;
}

export function parseMemUsageDpdkTotal(response) {
  const [header, ...rows] = response.trim(/\s/).split("\n");
  const output = {};

  for(const row of rows){
    const attrs = row.trim().split(/\s+/);
    if(!isNaN(attrs[0])){
      const bqnId = attrs[0];
      const value = attrs[3] == "n/a" ? 0.0 : Number(attrs[3])
      if(output[bqnId]){
        output[bqnId].push(value)
      }else{
        output[bqnId] = [value]
      }
    }
  }

  return output;
}

function parserUserItemF(it, i, totals) {
  return (it * 100) / totals[i - 1];
}

export const parseStatsMemData = (response, period, totals) => {
  const [header, ...rows] = response.trim(/\s/).split("\n");
  const [, ...keys] = header.split(/\s+/);
  const keysSeries = keys.map((key) => {
    const chunks = key.split("-").map((item) => {
      return item.charAt(0).toUpperCase() + item.slice(1).toLowerCase();
    });
    chunks.pop();
    return chunks.join("-");
  });

  const newKeys = ["time", ...keysSeries];
  const { System, Mpool, Dpdk } = totals;

  if (rows.length === 0) {
    return { fields: keysSeries, items: [] };
  }

  const newRows = rows.map((row) => {
    return row.split(/\s+/).reduce((acc, value, index) => {
      if (index === 0) {
        acc[newKeys[index]] = parseDate(value);
      } else {
        acc[newKeys[index]] = parserUserItemF(value, index, [
          System,
          Mpool,
          ...Dpdk,
        ]);
      }
      return acc;
    }, {});
  });

  return { fields: keysSeries, items: newRows };
};

export const getMemoryStatsLines = (lines = 288, mode, bqnId) =>
  ifCl.run(atBqnTarget(`show statistics memory lines ${lines}`, mode, bqnId))

// The commands of the memory of a BQN, in a single batch request: the CLI
// runs the commands of the GUI one after the other, so one request each would
// take one turn of its queue each. The statistics of the lines go with them
// when the caller gives their number.
const composeMemoryBatch = (mode, bqnId, lines) =>
  `${[
    atBqnTarget("show system memory", mode, bqnId),
    atBqnTarget("show pkteng memory", mode, bqnId),
    atBqnTarget("show pkteng memory dpdk", mode, bqnId),
    atBqnTarget("show system date", mode, bqnId),
    ...(lines === undefined
      ? []
      : [atBqnTarget(`show statistics memory lines ${lines}`, mode, bqnId)]),
  ].join("\n")}\n`;

// The outputs come back in the order of the commands. The data is indexed by
// BQN, so bqnIdShown is the BQN of the response of "show system memory",
// which reports no BQN ID when it is asked for one. The BQN selected is the
// one asked for, which is no BQN when the whole cluster is shown, so that
// every BQN of the cluster reports its own data.
const parseMemoryBatch = (response, bqnIdShown) => {
  const [system, pool, dpdk, date, stats] = outputsOfBatch(response);

  return [
    parseClusterResponse(system, parseMemSystemTotal, bqnIdShown),
    parseMemUsagePoolTotal(pool),
    parseMemUsageDpdkTotal(dpdk),
    parseSystemDate(date),
    stats,
  ];
};

export const memoryUsageSystemDateRetrieval = (bqnIdShown, mode, bqnId) =>
  ifCl
    .run(composeMemoryBatch(mode, bqnId), "batch_exmode")
    .then((response) => parseMemoryBatch(response, bqnIdShown));

// The memory of a BQN and the statistics of its lines, for a view that draws
// both: they take one turn of the queue together.
export const memoryUsageAndStatsRetrieval = (bqnIdShown, mode, bqnId, lines) =>
  ifCl
    .run(composeMemoryBatch(mode, bqnId, lines), "batch_exmode")
    .then((response) => parseMemoryBatch(response, bqnIdShown));
